<template>
  <v-container class="identity-page px-6 px-md-10 py-8">
    <v-alert
      v-if="error"
      type="error"
      outlined
      class="mb-6"
    >
      {{ error }}
    </v-alert>

    <div v-if="loading">
      <v-skeleton-loader type="card, card, card, card" class="mb-8" />
      <v-skeleton-loader type="list-item-three-line, list-item-three-line" />
    </div>

    <div v-else>
      <div class="identity-header mb-10">
        <div class="title-wrap">
          <div class="brand-icon">
            <v-icon color="#1876d2">mdi-shield-outline</v-icon>
          </div>
          <div>
            <h1>Validación de Identidad</h1>
            <p>Análisis de riesgo comercial con IA para empresas y personas</p>
          </div>
        </div>

        <v-btn
          depressed
          class="new-query-button"
          @click="handleNewQuery"
        >
          <v-icon left small>mdi-magnify</v-icon>
          Nueva consulta
        </v-btn>
      </div>
      <section class="mb-10">
        <v-row>
          <v-col
            v-for="metric in metrics"
            :key="metric.label"
            cols="12"
            sm="6"
            lg="3"
          >
            <v-card class="metric-card pa-6" elevation="2">
              <div class="metric-top">
                <p>{{ metric.label }}</p>
                <v-icon :color="metric.color">{{ metric.icon }}</v-icon>
              </div>

              <strong>{{ metric.value }}</strong>
              <span :class="{ positive: metric.positive }">{{ metric.caption }}</span>
            </v-card>
          </v-col>
        </v-row>
      </section>

      <section class="mb-10">
        <div class="tabs-wrap">
          <button
            v-for="tab in tabs"
            :key="tab.value"
            type="button"
            :disabled="tab.disabled"
            :class="{ active: activeTab === tab.value, disabled: tab.disabled }"
            @click="!tab.disabled && (activeTab = tab.value)"
          >
            {{ tab.label }}
          </button>
        </div>
      </section>

      <section v-if="activeTab === 'history'">
        <v-card class="queries-card pa-6" elevation="2">
          <div class="queries-header mb-7">
            <h2>Consultas recientes</h2>
            <div>
              <v-menu v-model="filterMenu" offset-y :close-on-content-click="false">
                <template #activator="{ on, attrs }">
                  <v-btn outlined class="utility-button mr-2" v-bind="attrs" v-on="on">
                    Filtrar
                    <v-icon right small v-if="hasActiveFilters">mdi-filter-check</v-icon>
                  </v-btn>
                </template>
                <v-card class="pa-4" style="min-width: 260px;">
                  <p class="filter-label mb-1">Tipo</p>
                  <v-select
                    v-model="filters.type"
                    :items="[
                      { text: 'Todos', value: 'all' },
                      { text: 'Empresa', value: 'company' },
                      { text: 'Persona', value: 'person' },
                    ]"
                    dense
                    outlined
                    color="#1876d2"
                    item-color="#1876d2"
                    hide-details
                    class="mb-4"
                  />
                  <p class="filter-label mb-1">Nivel de riesgo</p>
                  <v-select
                    v-model="filters.riskLevel"
                    :items="[
                      { text: 'Todos', value: 'all' },
                      { text: 'Bajo', value: 'bajo' },
                      { text: 'Medio', value: 'medio' },
                      { text: 'Alto', value: 'alto' },
                    ]"
                    dense
                    outlined
                    color="#1876d2"
                    item-color="#1876d2"
                    hide-details
                    class="mb-3"
                  />
                  <v-btn text small color="#1876d2" @click="clearFilters">
                    Limpiar filtros
                  </v-btn>
                </v-card>
              </v-menu>
            </div>
          </div>

          <div v-if="filteredQueries.length">
            <div
              v-for="query in filteredQueries"
              :key="query.id"
              class="query-row"
            >
              <div class="query-left">
                <div class="query-icon" :class="query.iconClass">
                  <v-icon :color="query.iconColor">{{ query.icon }}</v-icon>
                </div>

                <div>
                  <div class="query-name-line">
                    <h3>{{ query.name }}</h3>
                    <v-chip small label outlined class="entity-chip">
                      {{ query.type }}
                    </v-chip>
                  </div>
                  <p>
                    {{ query.identifier }}
                    <span>•</span>
                    {{ query.timeAgo }}
                  </p>
                </div>
              </div>

              <div class="query-right">
                <div class="score-wrap">
                  <span>Score:</span>
                  <strong :class="query.scoreClass">{{ query.score }}</strong>
                </div>
                <v-chip
                  small
                  label
                  :color="query.riskColor"
                  :text-color="query.riskTextColor"
                  class="risk-chip"
                >
                  {{ query.risk }}
                </v-chip>
              </div>

              <v-btn
                outlined
                class="report-button"
                @click="handleViewReport(query)"
              >
                <v-icon left small>mdi-eye-outline</v-icon>
                Ver reporte
              </v-btn>
            </div>
          </div>

          <v-card v-else class="empty-card pa-6" outlined>
            {{ recentQueries.length ? 'Ningún resultado coincide con los filtros aplicados.' : 'Todavía no hay consultas registradas.' }}
          </v-card>
        </v-card>
      </section>

      <section v-else-if="activeTab === 'alerts'">
        <v-card class="queries-card pa-6" elevation="2">
          <h2 class="mb-7">Alertas activas</h2>

          <div
            v-for="alert in activeAlerts"
            :key="alert.id"
            class="simple-row"
          >
            <div class="simple-icon alert-icon">
              <v-icon color="#FF4D00">mdi-alert-outline</v-icon>
            </div>
            <div>
              <h3>{{ alert.title }}</h3>
              <p>{{ alert.description }}</p>
            </div>
          </div>
        </v-card>
      </section>

      <section v-else>
        <v-card class="queries-card pa-6" elevation="2">
          <h2 class="mb-7">Reportes guardados</h2>

          <div
            v-for="report in savedReports"
            :key="report.id"
            class="simple-row"
          >
            <div class="simple-icon report-icon">
              <v-icon color="#2163FF">mdi-file-document-outline</v-icon>
            </div>
            <div>
              <h3>{{ report.title }}</h3>
              <p>{{ report.date }} • {{ report.type }}</p>
            </div>
          </div>
        </v-card>
      </section>
    </div>
  </v-container>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DueDiligence',
  data() {
    return {
      loading: false,
      error: null,
      activeTab: 'history',
      tabs: [
        {
          label: 'Historial de consultas',
          value: 'history',
        },
        {
          label: 'Alertas activas',
          value: 'alerts',
        },
        {
          label: 'Reportes guardados',
          value: 'reports',
          disabled: true,
        },
      ],
      metrics: [
        {
          label: 'Consultas este mes',
          value: '0',
          caption: '',
          icon: 'mdi-magnify',
          color: '#2163FF',
          positive: true,
        },
        {
          label: 'Riesgo bajo detectado',
          value: '0',
          caption: '',
          icon: 'mdi-check-circle-outline',
          color: '#00A342',
          positive: false,
        },
        {
          label: 'Riesgo alto detectado',
          value: '0',
          caption: '',
          icon: 'mdi-alert-outline',
          color: '#FF2F1F',
          positive: false,
        },
        {
          label: 'En proceso',
          value: '0',
          caption: '',
          icon: 'mdi-clock-outline',
          color: '#FF4D00',
          positive: false,
        },
      ],
      recentQueries: [],
      activeAlerts: [],
      filterMenu: false,
      filters: {
        type: 'all',
        riskLevel: 'all',
      },
      savedReports: [
        {
          id: 'report-1',
          title: 'Reporte Distribuidora ABC',
          date: '06 May 2026',
          type: 'Empresa',
        },
        {
          id: 'report-2',
          title: 'Reporte María Elena Rodríguez López',
          date: '04 May 2026',
          type: 'Persona',
        },
      ],
    };
  },
  computed: {
    selectLanguage() {
      return this.$store.state.language;
    },
    hasActiveFilters() {
      return this.filters.type !== 'all' || this.filters.riskLevel !== 'all';
    },
    filteredQueries() {
      return this.recentQueries.filter((query) => {
        const matchesType = this.filters.type === 'all' || query.typeRaw === this.filters.type;
        const matchesRisk = this.filters.riskLevel === 'all' || query.riskLevelRaw === this.filters.riskLevel;
        return matchesType && matchesRisk;
      });
    },
  },
  watch: {
    selectLanguage() {
      this.getReports();
    },
  },
  created() {
    this.getReports();
  },
  methods: {
    async getReports() {
      this.loading = true;
      this.error = null;
      const requestConfig = {
        headers: { Authorization: `Bearer ${this.$store.state.sToken}` },
      };

      try {
        const [localResult, middeskResult] = await Promise.allSettled([
          DB.get(`${URI}/api/v1/${this.selectLanguage}/check/reports`, requestConfig),
          DB.get(`${URI}/api/v1/${this.selectLanguage}/middesk/consultations`, requestConfig),
        ]);

        let localQueries = [];
        let middeskQueries = [];

        if (localResult.status === 'fulfilled') {
          const { companyReports = [], personReports = [] } = localResult.value.data.data || {};
          localQueries = [
            ...companyReports.map((report) => this.mapReportToQuery(report, 'company')),
            ...personReports.map((report) => this.mapReportToQuery(report, 'person')),
          ];
        }

        if (middeskResult.status === 'fulfilled') {
          const consultations = Array.isArray(middeskResult.value.data.data)
            ? middeskResult.value.data.data
            : [];
          middeskQueries = consultations.map(
            (consultation) => this.mapMiddeskConsultationToQuery(consultation)
          );
        }

        this.recentQueries = [...localQueries, ...middeskQueries].sort(
          (a, b) => b.createdAtRaw - a.createdAtRaw
        );
        this.metrics = this.buildMetrics(this.recentQueries);
        this.activeAlerts = this.buildActiveAlerts(this.recentQueries);

        if (localResult.status === 'rejected' && middeskResult.status === 'rejected') {
          this.error = 'No se pudieron cargar las consultas de Due Diligence';
        } else if (localResult.status === 'rejected') {
          this.error = 'No se pudieron cargar las consultas de México';
        } else if (middeskResult.status === 'rejected') {
          this.error = middeskResult.reason.response?.data?.message
            || 'No se pudieron cargar las consultas de Estados Unidos';
        }
      } finally {
        this.loading = false;
      }
    },
    buildMetrics(allQueries) {
      const total = allQueries.length;

      const now = moment();
      const thisMonthCount = allQueries.filter(
        (query) => query.createdAtRaw && moment(query.createdAtRaw).isSame(now, 'month')
      ).length;
      const lastMonthCount = allQueries.filter(
        (query) => query.createdAtRaw
          && moment(query.createdAtRaw).isSame(moment(now).subtract(1, 'month'), 'month')
      ).length;

      let monthCaption = '';
      let monthPositive = true;
      if (lastMonthCount > 0) {
        const change = Math.round(((thisMonthCount - lastMonthCount) / lastMonthCount) * 100);
        monthCaption = `${change >= 0 ? '+' : ''}${change}% vs mes anterior`;
        monthPositive = change >= 0;
      } else if (thisMonthCount > 0) {
        monthCaption = 'Sin consultas el mes anterior';
      }

      const lowCount = allQueries.filter((query) => query.riskLevelRaw === 'bajo').length;
      const highCount = allQueries.filter((query) => query.riskLevelRaw === 'alto').length;
      const processingStatuses = ['creating', 'processing', 'confirmation_pending', 'pending', 'in_audit', 'in_review'];
      const inProgressCount = allQueries.filter(
        (query) => processingStatuses.includes(String(query.statusRaw || '').toLowerCase())
      ).length;

      const pctOfTotal = (count) => (total > 0 ? `${Math.round((count / total) * 100)}% del total` : 'Sin consultas registradas');

      return [
        {
          label: 'Consultas este mes',
          value: String(thisMonthCount),
          caption: monthCaption,
          icon: 'mdi-magnify',
          color: '#2163FF',
          positive: monthPositive,
        },
        {
          label: 'Riesgo bajo detectado',
          value: String(lowCount),
          caption: pctOfTotal(lowCount),
          icon: 'mdi-check-circle-outline',
          color: '#00A342',
          positive: false,
        },
        {
          label: 'Riesgo alto detectado',
          value: String(highCount),
          caption: pctOfTotal(highCount),
          icon: 'mdi-alert-outline',
          color: '#FF2F1F',
          positive: false,
        },
        {
          label: 'En proceso',
          value: String(inProgressCount),
          caption: '',
          icon: 'mdi-clock-outline',
          color: '#FF4D00',
          positive: false,
        },
      ];
    },
    mapReportToQuery(report, type) {
      moment.locale(this.selectLanguage === 'sp' ? 'es' : 'en');
      const isCompany = type === 'company';
      const riskMeta = this.getRiskMeta(report.risk_level);

      return {
        id: report.sId,
        type: isCompany ? 'Empresa' : 'Persona',
        typeRaw: type,
        statusRaw: report.status || null,
        riskLevelRaw: report.risk_level || null,
        hasSanctionMatch: Boolean(report.has_sanction_match),
        name: isCompany
          ? report.razon_social || 'Sin razón social'
          : [report.nombres, report.apellido_paterno, report.apellido_materno].filter(Boolean).join(' ') || 'Sin nombre',
        identifier: report.rfc || report.curp || '—',
        timeAgo: report.created_at ? moment(report.created_at).format('DD MMM YYYY, hh:mm A') : '—',
        createdAtRaw: report.created_at ? new Date(report.created_at).getTime() : 0,
        score: report.risk_score != null ? String(report.risk_score) : '—',
        scoreClass: riskMeta.scoreClass,
        risk: riskMeta.label,
        riskColor: riskMeta.riskColor,
        riskTextColor: riskMeta.riskTextColor,
        icon: isCompany ? 'mdi-office-building-outline' : 'mdi-account-outline',
        iconClass: isCompany ? 'query-icon-blue' : 'query-icon-purple',
        iconColor: isCompany ? '#2163FF' : '#1F5FA6',
      };
    },
    mapMiddeskConsultationToQuery(consultation) {
      moment.locale(this.selectLanguage === 'sp' ? 'es' : 'en');
      const visibleStatus = consultation.status === 'report_available'
        ? consultation.providerStatus || consultation.status
        : consultation.status;
      const statusMeta = this.getMiddeskStatusMeta(visibleStatus);

      return {
        id: consultation.id,
        provider: 'middesk',
        country: 'US',
        isMiddesk: true,
        type: 'Empresa · EE. UU.',
        typeRaw: 'company',
        statusRaw: visibleStatus || null,
        riskLevelRaw: null,
        hasSanctionMatch: false,
        name: consultation.businessName || 'Empresa sin nombre',
        identifier: consultation.providerBusinessId || consultation.id || '—',
        timeAgo: consultation.createdAt
          ? moment(consultation.createdAt).format('DD MMM YYYY, hh:mm A')
          : '—',
        createdAtRaw: consultation.createdAt
          ? new Date(consultation.createdAt).getTime()
          : 0,
        score: '—',
        scoreClass: '',
        risk: statusMeta.label,
        riskColor: statusMeta.color,
        riskTextColor: statusMeta.textColor,
        icon: 'mdi-domain',
        iconClass: 'query-icon-blue',
        iconColor: '#2163FF',
      };
    },
    getMiddeskStatusMeta(status) {
      const statuses = {
        creating: { label: 'Registrando', color: '#E8F0FF', textColor: '#2163FF' },
        processing: { label: 'En proceso', color: '#E8F0FF', textColor: '#2163FF' },
        confirmation_pending: { label: 'Confirmación pendiente', color: '#FFF3CD', textColor: '#8A6D3B' },
        report_available: { label: 'Reporte disponible', color: '#D9FAE4', textColor: '#008A38' },
        failed: { label: 'No procesada', color: '#FFE0E0', textColor: '#E60012' },
        open: { label: 'Abierta', color: '#EEF0F4', textColor: '#465266' },
        pending: { label: 'En proceso', color: '#E8F0FF', textColor: '#2163FF' },
        in_audit: { label: 'En auditoría', color: '#E8F0FF', textColor: '#2163FF' },
        in_review: { label: 'Revisión', color: '#FFF3CD', textColor: '#8A6D3B' },
        approved: { label: 'Aprobada', color: '#D9FAE4', textColor: '#008A38' },
        rejected: { label: 'Rechazada', color: '#FFE0E0', textColor: '#E60012' },
      };
      return statuses[status] || { label: status || 'Sin evaluar', color: '#EEF0F4', textColor: '#465266' };
    },
    getRiskMeta(riskLevel) {
      switch (riskLevel) {
        case 'alto':
          return {
            label: 'Riesgo alto', scoreClass: 'score-high',
            riskColor: '#FFE0E0', riskTextColor: '#E60012',
          };
        case 'medio':
          return {
            label: 'Riesgo medio', scoreClass: 'score-medium',
            riskColor: '#FFE6C9', riskTextColor: '#FF4D00',
          };
        case 'bajo':
          return {
            label: 'Riesgo bajo', scoreClass: 'score-low',
            riskColor: '#D9FAE4', riskTextColor: '#00A342',
          };
        default:
          return {
            label: 'Sin evaluar', scoreClass: '',
            riskColor: '#E7E9EE', riskTextColor: '#465266',
          };
      }
    },
    handleNewQuery() {
      this.$router.push('/dueDiligence/new');
    },
    clearFilters() {
      this.filters.type = 'all';
      this.filters.riskLevel = 'all';
    },
    buildActiveAlerts(queries) {
      return queries
        .filter((query) => query.riskLevelRaw === 'alto' || query.hasSanctionMatch)
        .map((query) => ({
          id: query.id,
          title: query.hasSanctionMatch
            ? `Coincidencia en listas de sanciones — ${query.name}`
            : `${query.name} — Riesgo alto`,
          description: `${query.identifier} • ${query.timeAgo}`,
        }));
    },
    handleViewReport(query) {
      if (query.isMiddesk) {
        this.$router.push({
          path: '/dueDiligence/new',
          query: { provider: 'middesk', consultationId: query.id },
        });
        return;
      }

      const type = query.type === 'Empresa' ? 'empresa' : 'persona';
      this.$router.push(`/dueDiligence/${query.id}?type=${type}`);
    },
  },
};
</script>

<style scoped>
.identity-page {
  min-height: 100vh;
  background: #f7f8fa;
  color: #0c0d10;
}

.identity-header,
.title-wrap,
.queries-header,
.query-name-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
}

.title-wrap,
.query-name-line {
  justify-content: flex-start;
}

.brand-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 54px;
  height: 54px;
  border-radius: 16px;
  background: #eaf4fd;
}

.identity-header h1 {
  font-family: 'montserrat-bold';
  font-size: 40px;
  line-height: 1.15;
  margin-bottom: 12px;
  color: #0c0d10;
}

.identity-header p {
  font-family: 'montserrat-semibold';
  font-size: 21px;
  color: #465266;
  margin-bottom: 0;
}

.new-query-button {
  min-width: 214px;
  height: 48px !important;
  border-radius: 8px;
  background: #1876d2 !important;
  color: white !important;
  font-family: 'montserrat-bold';
  font-size: 16px;
  text-transform: none;
}

.metric-card,
.queries-card,
.empty-card {
  border-radius: 16px;
  background: white;
}

.metric-card {
  min-height: 238px;
}

.metric-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 48px;
}

.metric-top p {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin-bottom: 0;
}

.metric-card strong {
  display: block;
  margin-bottom: 34px;
  font-family: 'montserrat-bold';
  font-size: 34px;
}

.metric-card span {
  font-family: 'montserrat-semibold';
  font-size: 16px;
  color: #465266;
}

.metric-card span.positive {
  color: #00a342;
}

.tabs-wrap {
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  border-radius: 18px;
  background: #e7e9ee;
}

.tabs-wrap button {
  height: 39px;
  padding: 0 16px;
  border: 0;
  border-radius: 16px;
  background: transparent;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  cursor: pointer;
}

.tabs-wrap button.active {
  background: white;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.tabs-wrap button.disabled,
.tabs-wrap button:disabled {
  color: #9CA3AF;
  cursor: not-allowed;
}

.queries-card h2 {
  font-family: 'montserrat-bold';
  font-size: 25px;
  margin-bottom: 0;
}

.utility-button,
.report-button {
  height: 44px !important;
  border-color: #d8d9df;
  border-radius: 8px;
  background: white;
  font-family: 'montserrat-bold';
  font-size: 16px;
  color: #111111;
  text-transform: none;
}

.query-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  gap: 22px;
  align-items: center;
  padding: 26px 20px;
  margin-bottom: 16px;
  border: 1px solid #dfe3ea;
  border-radius: 16px;
}

.query-row:last-child {
  margin-bottom: 0;
}

.query-left {
  display: flex;
  align-items: center;
  gap: 22px;
  min-width: 0;
}

.query-icon,
.simple-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  height: 64px;
  border-radius: 16px;
}

.query-icon-blue {
  background: #d8e8ff;
}

.query-icon-purple {
  background: #eadcff;
}

.query-left h3,
.simple-row h3 {
  font-family: 'montserrat-bold';
  font-size: 22px;
  margin-bottom: 8px;
}

.query-left p,
.simple-row p {
  display: flex;
  align-items: center;
  gap: 18px;
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
  margin-bottom: 0;
}

.entity-chip,
.risk-chip {
  font-family: 'montserrat-bold';
}

.query-right {
  min-width: 126px;
}

.score-wrap {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
}

.score-wrap span {
  font-family: 'montserrat-semibold';
  font-size: 18px;
  color: #465266;
}

.score-wrap strong {
  font-family: 'montserrat-bold';
  font-size: 32px;
}

.score-low {
  color: #00a342;
}

.score-medium {
  color: #ff4d00;
}

.score-high {
  color: #e60012;
}

.simple-row {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 22px 0;
  border-bottom: 1px solid #edf0f3;
}

.simple-row:last-child {
  border-bottom: 0;
}

.alert-icon {
  background: #ffe6c9;
}

.report-icon {
  background: #d8e8ff;
}

.empty-card {
  font-family: 'montserrat-semibold';
  color: #626d80;
}

.filter-label {
  font-family: 'montserrat-semibold';
  font-size: 14px;
  color: #465266;
}

@media (max-width: 960px) {
  .identity-page {
    padding-left: 20px !important;
    padding-right: 20px !important;
  }

  .identity-header,
  .title-wrap,
  .queries-header,
  .query-row {
    align-items: flex-start;
  }

  .identity-header {
    flex-direction: column;
  }

  .identity-header h1 {
    font-size: 32px;
  }

  .identity-header p {
    font-size: 18px;
  }

  .new-query-button {
    width: 100%;
  }

  .tabs-wrap {
    display: flex;
    width: 100%;
    overflow-x: auto;
  }

  .tabs-wrap button {
    white-space: nowrap;
  }

  .queries-header {
    flex-direction: column;
  }

  .query-row {
    grid-template-columns: 1fr;
  }

  .query-left {
    align-items: flex-start;
  }

  .query-name-line {
    flex-wrap: wrap;
  }

  .report-button,
  .utility-button {
    width: 100%;
  }
}

/* Fresher AI shared visual language */
.identity-page {
  width: 100%;
  max-width: 1740px !important;
  margin: 0 auto;
  padding: 28px 40px 32px !important;
  font-family: 'montserrat-regular';
}

.identity-header {
  margin-bottom: 28px !important;
}

.brand-icon {
  min-width: 46px;
  height: 46px;
  border-radius: 12px;
  background: #eaf4fd;
}

.identity-header h1 {
  margin-bottom: 5px;
  font-size: 28px;
  line-height: 1.2;
}

.identity-header p {
  font-family: 'montserrat-regular';
  font-size: 14px;
  color: #718096;
}

.new-query-button {
  min-width: 184px;
  height: 42px !important;
  border-radius: 8px;
  background: #1876d2 !important;
  font-size: 13px;
}

.metric-card,
.queries-card,
.empty-card {
  min-height: auto;
  border: 1px solid #e2e7ed !important;
  border-radius: 12px !important;
  box-shadow: 0 3px 12px rgba(30, 47, 70, 0.04) !important;
}

.metric-card {
  height: 100%;
  padding: 20px !important;
}

.metric-top {
  margin-bottom: 20px;
}

.metric-top p {
  font-size: 13px;
}

.metric-card strong {
  margin-bottom: 12px;
  font-size: 28px;
}

.metric-card span {
  font-size: 12px;
}

.tabs-wrap button {
  font-size: 13px;
}

.query-row {
  padding-top: 18px;
  padding-bottom: 18px;
}

@media (max-width: 960px) {
  .identity-page {
    padding: 24px !important;
  }

  .identity-header h1 {
    font-size: 26px;
  }

  .identity-header p {
    font-size: 13px;
  }
}

@media (max-width: 600px) {
  .identity-page {
    padding: 20px 16px 28px !important;
  }
}
</style>
